"use client"

import * as React from "react"
import * as Icons from "lucide-react"
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  SidebarProvider,
  useSidebar,
  SidebarFooter,
} from "@/components/ui/sidebar"
import { NavReports } from "./nav-reports"
import { useQuery, useQueryClient } from '@tanstack/react-query';
import FillTask from "@/components/features/tasks/fill";
import { AlertDialog, AlertDialogContent, AlertDialogCancel, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { Button } from "@/components/ui/button"
import { useTRPC } from "@/trpc"
import { toast } from "sonner"
import type { QueryKey } from "@tanstack/react-query";

export interface RecentEditedPlayerEntry {
  task_id: string;
  player_id: string;
  player_name: string;
}

interface FieldResponse {
  fieldId: string;
  value: string;
  fieldType?: string;
}

interface DraftData {
  fieldResponses: FieldResponse[];
  feedback?: string;
  player?: { id: string; name: string };
}

interface TaskCacheItem {
  feedback?: string | null;
  localFieldResponses?: FieldResponse[];
  completed: boolean;
}

type DialogMode = 'remove' | 'saveAll';
type DraftQueryKey = readonly ['tasks', 'byId', string, 'draft'];
type DraftEntry = [DraftQueryKey, DraftData];

const RECENT_KEY = ['recent-edited-players'] as const;

function isDraftQuery(queryKey: QueryKey): queryKey is DraftQueryKey {
  return (
    Array.isArray(queryKey) &&
    queryKey.length === 4 &&
    queryKey[0] === 'tasks' &&
    queryKey[1] === 'byId' &&
    typeof queryKey[2] === 'string' &&
    queryKey[3] === 'draft'
  );
}

function RightSideBarInner({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { open } = useSidebar()
  const qc = useQueryClient();
  const { trpc } = useTRPC()
  
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [toRemove, setToRemove] = React.useState<RecentEditedPlayerEntry | null>(null)
  const [isSavingAll, setIsSavingAll] = React.useState(false)
  const [dialogMode, setDialogMode] = React.useState<DialogMode | null>(null)
  const [activeTaskId, setActiveTaskId] = React.useState<string | null>(null)
  const [fillOpen, setFillOpen] = React.useState(false)

  const { data: recent = [] } = useQuery<RecentEditedPlayerEntry[]>({
    queryKey: RECENT_KEY,
    initialData: () => qc.getQueryData(RECENT_KEY) ?? [],
    enabled: false,
  })

  const handleSelect = React.useCallback((task_id: string) => {
    setActiveTaskId(task_id)
    setFillOpen(true)
  }, [])

  const getDraftEntries = React.useCallback((): DraftEntry[] => {
    const draftTuples = qc.getQueriesData<DraftData>({
      predicate: (q) => isDraftQuery(q.queryKey),
    });

    return draftTuples
      .filter((entry): entry is DraftEntry => {
        const [key, data] = entry;
        return isDraftQuery(key) && data !== undefined;
      });
  }, [qc])

  const submitTask = React.useCallback(async (taskId: string, draft: DraftData) => {
    await trpc.tasks.submit.mutate({
      task_id: taskId,
      feedback: draft.feedback,
      fields: draft.fieldResponses.map(fr => ({
        field_id: fr.fieldId,
        answer: fr.value,
      })),
    });

    qc.setQueryData<TaskCacheItem>(
      ['tasks', 'byId', taskId],
      (old) =>
        old
          ? {
              ...old,
              feedback: draft.feedback ?? null,
              localFieldResponses: draft.fieldResponses,
              completed: true
            }
          : old
    );

    qc.removeQueries({
      queryKey: ['tasks', 'byId', taskId, 'draft'],
      exact: true
    });

    qc.setQueryData<RecentEditedPlayerEntry[]>(
      RECENT_KEY,
      (old) => (old ?? []).filter(e => e.task_id !== taskId)
    );

    if (activeTaskId === taskId) {
      setFillOpen(false);
      setActiveTaskId(null);
    }
  }, [qc, trpc, activeTaskId])

  const confirmSaveAll = React.useCallback(async () => {
    const entries = getDraftEntries();
    
    if (entries.length === 0) {
      toast.info('Geen bewerkingen om op te slaan.');
      return;
    }

    setIsSavingAll(true);

    const results = await Promise.allSettled(
      entries.map(([key, draft]) => submitTask(key[2], draft))
    );

    const failures = results.filter(r => r.status === 'rejected').length;
    const successes = results.length - failures;

    await Promise.all([
      qc.invalidateQueries({ queryKey: ['tasks'] }),
      qc.invalidateQueries({ queryKey: ['assigned-tasks'] }),
    ]);

    if (failures > 0) {
      toast.error(`${successes} opgeslagen, ${failures} mislukt.`);
    } else {
      toast.success(`Alle ${entries.length} bewerkingen opgeslagen.`);
    }

    setIsSavingAll(false);
  }, [getDraftEntries, submitTask, qc]);

  const handleRemove = React.useCallback((task_id: string) => {
    const entry = recent.find(r => r.task_id === task_id) ?? {
      task_id,
      player_id: "",
      player_name: "",
    }
    setToRemove(entry)
    setDialogMode('remove')
    setConfirmOpen(true)
  }, [recent])

  const confirmRemove = React.useCallback(() => {
    if (!toRemove) return;
    
    const { task_id } = toRemove;
    
    qc.setQueryData<RecentEditedPlayerEntry[]>(
      RECENT_KEY,
      (old) => (old ?? []).filter(e => e.task_id !== task_id)
    );
    
    qc.removeQueries({
      queryKey: ['tasks', 'byId', task_id, 'draft'],
      exact: true
    });

    if (activeTaskId === task_id) {
      setFillOpen(false);
      setActiveTaskId(null);
    }
    
    toast.success(`Bewerking voor "${toRemove.player_name}" verwijderd.`);
    setConfirmOpen(false);
    setToRemove(null);
    setDialogMode(null);
  }, [toRemove, qc, activeTaskId])

  const handleDialogClose = React.useCallback((isOpen: boolean) => {
    if (isSavingAll) return;
    setConfirmOpen(isOpen);
    if (!isOpen) {
      setDialogMode(null);
      setToRemove(null);
    }
  }, [isSavingAll])

  const handleSaveAllClick = React.useCallback(() => {
    setDialogMode('saveAll');
    setConfirmOpen(true);
  }, [])

  const handleFillTaskSuccess = React.useCallback(() => {
    confirmRemove();
    setFillOpen(false);
    setActiveTaskId(null);
  }, [confirmRemove])

  return (
    <>
      <Sidebar side="right" variant="inset" collapsible="icon" {...props}>
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem className="flex items-center justify-between">
              {open && <SidebarMenuButton size="lg" asChild />}
              <SidebarTrigger className="w-8 h-8" />
            </SidebarMenuItem>
            {!open && (
              <SidebarMenuItem>
                <SidebarMenuButton disabled>
                  {`(${recent.length})`}
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
          <NavReports
            label="Openstaande bewerkingen"
            items={recent}
            onSelect={handleSelect}
            onRemove={handleRemove}
          />
        </SidebarContent>
        
        <SidebarFooter>
          {!open && (
            <SidebarMenuButton
              size="lg"
              asChild
              onClick={handleSaveAllClick}
              disabled={isSavingAll}
              className="p-0 flex items-center justify-center cursor-pointer" 
            >
              <span className="w-8 h-8 flex items-center justify-center">
                <Icons.Save className="w-5 h-5" /> 
              </span>
            </SidebarMenuButton>
          )}
          {open && (
            <Button onClick={handleSaveAllClick} disabled={isSavingAll}>
              {isSavingAll && <Icons.Loader2 className="w-4 h-4 animate-spin" />}
              Alles opslaan
            </Button>
          )}
        </SidebarFooter>
      </Sidebar>

      {activeTaskId && (
        <FillTask
          task_id={activeTaskId}
          open={fillOpen}
          onOpenChange={setFillOpen}
          onSuccess={handleFillTaskSuccess}
        />
      )}

      <AlertDialog open={confirmOpen} onOpenChange={handleDialogClose}>
        <AlertDialogContent>
          {dialogMode === 'saveAll' ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Alles opslaan?</AlertDialogTitle>
                <AlertDialogDescription>
                  Weet je zeker dat je alle bewerkingen wilt opslaan?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isSavingAll}>
                  Annuleren
                </AlertDialogCancel>
                <Button
                  onClick={async () => {
                    await confirmSaveAll();
                    setConfirmOpen(false);
                    setDialogMode(null);
                  }}
                  disabled={isSavingAll}
                >
                  {isSavingAll && <Icons.Loader2 className="w-4 h-4 animate-spin" />}
                  Opslaan
                </Button>
              </AlertDialogFooter>
            </>
          ) : dialogMode === 'remove' ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Verwijder bewerking?</AlertDialogTitle>
                <AlertDialogDescription>
                  Weet je zeker dat je de bewerking voor "{toRemove?.player_name || 'deze speler'}" wilt verwijderen? Het rapport wordt gewist.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isSavingAll}>
                  Annuleren
                </AlertDialogCancel>
                <Button
                  variant='destructive'
                  onClick={confirmRemove}
                  disabled={isSavingAll}
                >
                  Verwijderen
                </Button>
              </AlertDialogFooter>
            </>
          ) : null}
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export function AppRightSidebar(props: React.ComponentProps<typeof Sidebar>) {
  return (
    <SidebarProvider defaultOpen={false}>
      <RightSideBarInner {...props} />
    </SidebarProvider>
  )
}